import { FilterInput } from '$components/Internal/Positions/Filters/FilterInput';
import IndeterminateCheckbox from '$components/Screens/SimeraCandidatesScreen/IndeterminateCheckbox/IndeterminateCheckbox';
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import {
  useState,
  useMemo,
  useEffect,
  ChangeEvent,
  useCallback,
  useRef,
  ReactChild,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SearchCandidateInput,
  SearchCandidateOutput,
  useLazySearchCandidateQuery,
  useSearchCandidateQuery,
} from '$store/modules/candidates';
import { ReactComponent as Export } from '$icons/export.svg';
import { ReactComponent as SortArrow } from '$icons/CloseArrowColor.svg';
import { ReactComponent as Arrow } from '$icons/ArrowBackInternal.svg';
import dayjs from 'dayjs';
import Spinner from '$components/Spinner/Spinner';
import { GroupedCountries } from '$constants/countries';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { SEARCH_CANDIDATES_SHORTDATE } from '$constants/datetimes';
import SearchCandidatesFilter from './SearchCandidatesFilter/SearchCandidatesFilter';

type IColumnSort = {
  column: 'id';
  sortType: 'ASC' | 'DESC';
};

const SortColumnButton = ({
  children,
  onClick,
  sort,
  columnName,
}: {
  children: ReactChild;
  onClick: () => void;
  sort: IColumnSort;
  columnName: string;
}) => {
  return (
    <button
      onClick={() => onClick()}
      type="button"
      className="flex flex-row items-center justify-center gap-2"
    >
      {children}
      {columnName === sort.column && (
        <SortArrow
          className={`w-4 h-4 ${
            sort.sortType === 'ASC' ? 'rotate-180' : 'rotate-0'
          }`}
        />
      )}
    </button>
  );
};

const SearchCandidates = () => {
  // Limit set on the backend side
  const SearchCandidatesMaxResultSize = 125;
  const [rowSelection, setRowSelection] = useState<{ number: boolean } | {}>(
    {}
  );
  const lastSearch = useRef('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get('take') || '100', 10)
  );
  const [columnSort, setColumnSort] = useState<IColumnSort>({
    column: 'id',
    sortType: 'DESC',
  });
  const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);

  const [t] = useTranslation('global');

  const searchCandidatesFilters = useMemo(() => {
    const searchParamsJSON = Object.fromEntries(searchParams.entries());
    const parsedCountryParams = searchParamsJSON?.Country?.split(',').reduce(
      (prev, act) => {
        const isGroupedCountry =
          Object.keys(GroupedCountries).indexOf(act) >= 0;
        if (isGroupedCountry) {
          prev.CountryGroup.push(act);
        } else {
          prev.Country.push(act);
        }
        return prev;
      },
      { CountryGroup: [] as string[], Country: [] as string[] }
    );

    return {
      ...searchParamsJSON,
      ...parsedCountryParams,
    };
  }, [searchParams]);

  /* Search used for the general page */
  const {
    data: SearchCandidateData,
    isLoading: isLoadingSearchCandidate,
    isFetching: isFetchingSearchCandidate,
  } = useSearchCandidateQuery(
    {
      // ...Object.fromEntries(searchParams.entries()),
      ...searchCandidatesFilters,
      orderBy: columnSort.column,
      Order: columnSort.sortType,
      page,
      take: pageSize,
    } as SearchCandidateInput,
    {
      skip: !searchParams.get('Keyword'),
    }
  );

  /* Search used for the export functionality */
  const [searchCandidate] = useLazySearchCandidateQuery();

  const totalPages = useMemo(
    () => Math.ceil((SearchCandidateData?.total || 0) / pageSize),
    [SearchCandidateData?.total, pageSize]
  );

  const exportCandidates = useCallback(async () => {
    let allSearchData;
    const selectedCandidates = Object.keys(rowSelection);

    // if already has fetched all the data or has selected only some rows
    if (
      SearchCandidateData!.total <= SearchCandidateData!.data.length ||
      selectedCandidates.length > 0
    ) {
      allSearchData = SearchCandidateData?.data?.filter((singleCandidate) => {
        // If user hasnt selected any candidate, let him export all the candidates
        if (selectedCandidates.length === 0) return true;

        // Otherwise, only export the selected candidates
        return selectedCandidates.indexOf(`${singleCandidate.Id}`) >= 0;
      });
    } else {
      setIsGeneratingCSV(true);

      const allSearchTotalPages = Math.ceil(
        SearchCandidateData!.total / SearchCandidatesMaxResultSize
      );

      const allSearchDataPromises = [];

      for (
        let searchPage = 1;
        searchPage <= allSearchTotalPages;
        searchPage += 1
      ) {
        allSearchDataPromises.push(
          searchCandidate({
            ...searchCandidatesFilters,
            orderBy: columnSort.column,
            Order: columnSort.sortType,
            page: searchPage,
            take: SearchCandidatesMaxResultSize,
          } as SearchCandidateInput)
        );
      }
      allSearchData = await Promise.all(allSearchDataPromises)
        .then((resp) => resp.map((singleResp) => singleResp!.data!.data))
        .then((resp) => resp.flat());

      setIsGeneratingCSV(false);
    }

    // If for some reason the endpoint doesnt return data dont download an empty csv
    if (!allSearchData) return;

    const csvHeaders = [
      'Candidate Id',
      'Email',
      'Name',
      'Last Name',
      `Create Date (${SEARCH_CANDIDATES_SHORTDATE})`,
      `Last Login (${SEARCH_CANDIDATES_SHORTDATE})`,
      'Country',
      `Last Job`,
      'Salary Range',
      'CV',
      'Video',
      'Reference',
      'Quality Status',
      'Open To Work',
      'Open To Work Last Update',
      'Skills',
      'Work History',
      'Education',
      'Job Titles',
    ].join(',');

    const csvContent =
      allSearchData!.map((singleCandidate) => {
        const candidateData: string[] = [];
        /* Candidate Id */
        candidateData.push(`${singleCandidate.Id}`);

        /* Candidate Email */
        candidateData.push(`"${singleCandidate.User?.Email}"`);

        /* Candidate Name */
        candidateData.push(`"${singleCandidate.User?.FirstName}"`);

        /* Candidate Last Name */
        candidateData.push(`"${singleCandidate.User?.LastName}"`);

        /* Create Date */
        candidateData.push(
          `"${dayjs(singleCandidate.CreateTime).format(
            SEARCH_CANDIDATES_SHORTDATE
          )}"`
        );

        /* Last Login */
        candidateData.push(
          singleCandidate.LastLogin
            ? `"${dayjs(singleCandidate.LastLogin).format(
                SEARCH_CANDIDATES_SHORTDATE
              )}"`
            : 'N/A'
        );

        /* Country */
        candidateData.push(
          `${
            singleCandidate.Country && 'Name' in singleCandidate.Country
              ? singleCandidate.Country.Name
              : 'N/A'
          }`
        );

        /* Last Job */
        candidateData.push(
          `${
            singleCandidate.CandidateWorkExperience &&
            singleCandidate.CandidateWorkExperience.length > 0 &&
            singleCandidate.CandidateWorkExperience[0].JobPosition &&
            singleCandidate.CandidateWorkExperience[0].JobPosition.title
              ? singleCandidate.CandidateWorkExperience[0].JobPosition.title.replace(
                  /(\r|\t|\n)/gi,
                  ''
                )
              : 'N/A'
          }`
        );

        /* Candidate Salary Range */
        if (
          !singleCandidate.CandidateJobInterest ||
          (!singleCandidate.CandidateJobInterest?.SalaryLowerBound &&
            !singleCandidate.CandidateJobInterest?.SalaryUpperBound)
        )
          candidateData.push('N/A');
        else
          candidateData.push(
            `${
              singleCandidate.CandidateJobInterest.SalaryLowerBound || 'N/A'
            } - ${
              singleCandidate.CandidateJobInterest.SalaryUpperBound || 'N/A'
            }`
          );

        /* CV */
        candidateData.push(singleCandidate.Curriculum ? 'TRUE' : 'FALSE');

        /* Video */
        candidateData.push(
          singleCandidate.AdditionalVideos &&
            singleCandidate.AdditionalVideos.length > 0
            ? 'TRUE'
            : 'FALSE'
        );

        /* Reference */
        candidateData.push(
          singleCandidate.Files &&
            singleCandidate.Files.length > 0 &&
            singleCandidate.Files.find(
              (singleFile) =>
                singleFile?.FileCategory?.CategoryName?.toLowerCase() ===
                'reference'
            )
            ? 'TRUE'
            : 'FALSE'
        );

        /* Quality Status */
        candidateData.push(singleCandidate.QualityStatus || 'N/A');

        /* Open To Work */
        candidateData.push(singleCandidate.OpenToWork || 'N/A');

        /* Open To Work Last Update */
        candidateData.push(
          singleCandidate.OpenToWorkUpdatedOn
            ? `"${dayjs(singleCandidate.OpenToWorkUpdatedOn).format(
                SEARCH_CANDIDATES_SHORTDATE
              )}"`
            : 'N/A'
        );

        /* Skills */
        candidateData.push(
          `"${singleCandidate?.CandidateSkills?.map(
            (singleSkill) => singleSkill.Skill.Title
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        /* Work History */
        candidateData.push(
          `"${singleCandidate?.CandidateWorkExperience?.map(
            (singleExp) => `${singleExp.JobPosition?.title}`
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        /* Education */
        candidateData.push(
          `"${singleCandidate?.CandidateEducation?.map(
            (singleEducation) => singleEducation.EducationalInstitutionName
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        /* Job Titles */
        candidateData.push(
          `"${singleCandidate?.CandidateWorkExperience?.map(
            (singleExp) => `${singleExp.JobTitle}`
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        return candidateData.join(',');
      }) || [];

    const blob = new Blob([[csvHeaders, ...csvContent].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'Simera - Candidates.csv';

    link.click();

    URL.revokeObjectURL(blobUrl);
  }, [
    SearchCandidateData,
    columnSort.column,
    columnSort.sortType,
    rowSelection,
    searchCandidate,
    searchCandidatesFilters,
  ]);

  const clickSortColumnHandler = useCallback(
    (columnName: string) => {
      const isOtherColumn = columnName !== columnSort.column;

      const newSort = {
        column: columnName,
        sortType:
          columnSort.sortType === 'ASC' || isOtherColumn ? 'DESC' : 'ASC',
      } as IColumnSort;
      setColumnSort(newSort);
    },
    [columnSort]
  );

  const columns = useMemo<ColumnDef<SearchCandidateOutput['data'][number]>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => {
          return (
            <div>
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
              />
            </div>
          );
        },
        cell: ({ row }) => {
          return (
            <div>
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </div>
          );
        },
      },
      {
        header: ({ column }) => (
          <SortColumnButton
            onClick={() => clickSortColumnHandler(column.id.toLowerCase())}
            sort={columnSort}
            columnName={column.id.toLowerCase()}
          >
            {`${t('SearchCandidates.Table.CandidateId')}`}
          </SortColumnButton>
        ),
        accessorKey: 'Id',
        cell: ({ cell }) => {
          return (
            <Link
              to={`/simera-dashboard/candidates/${cell.getValue()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex py-4 w-full h-full hover:underline hover:text-blue-500 hover:font-bold"
            >
              {cell.getValue()}
            </Link>
          );
        },
      },
      {
        header: `${t('SearchCandidates.Table.Email')}`,
        accessorKey: 'User.Email',
        id: 'Email',
      },
      {
        accessorKey: 'CreateDate',
        header: () => (
          <div className="text-left">
            <div>{t('SearchCandidates.Table.CreateDate')}</div>
            <div>({SEARCH_CANDIDATES_SHORTDATE})</div>
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div>{`${dayjs(row.original.CreateTime).format(
              SEARCH_CANDIDATES_SHORTDATE
            )}`}</div>
          );
        },
      },
      {
        accessorKey: 'LastLogin',
        header: () => (
          <div>
            <div>{t('SearchCandidates.Table.LastLogin')}</div>
            <div>({SEARCH_CANDIDATES_SHORTDATE})</div>
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div>
              {row.original.LastLogin &&
                `${dayjs(row.original.LastLogin).format(
                  SEARCH_CANDIDATES_SHORTDATE
                )}`}
            </div>
          );
        },
      },
      {
        accessorKey: 'Country.Name',
        header: `${t('SearchCandidates.Table.Country')}`,
        id: 'country',
      },
      {
        header: `${t('SearchCandidates.Table.LastJob')}`,
        id: 'lastJob',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.CandidateWorkExperience?.[0]?.JobPosition?.title ||
                ''}
            </div>
          );
        },
      },
      {
        header: `${t('SearchCandidates.Table.Salary')}`,
        cell: ({ row }) => {
          const { SalaryLowerBound, SalaryUpperBound } =
            row.original?.CandidateJobInterest || {};
          if (!SalaryLowerBound && !SalaryUpperBound) return null;
          return (
            <div>{`${SalaryLowerBound || 'N/A'} - ${
              SalaryUpperBound || 'N/A'
            }`}</div>
          );
        },
      },
      {
        id: 'cv',
        header: `${t('SearchCandidates.Table.CV')}`,
        cell: ({ row }) => {
          if (row.original?.Curriculum) return '✓';
          return null;
        },
      },
      {
        id: 'video',
        header: `${t('SearchCandidates.Table.Video')}`,
        cell: ({ row }) => {
          if (row.original?.AdditionalVideos?.length > 0) return '✓';
          return null;
        },
      },
      {
        header: `${t('SearchCandidates.Table.Reference')}`,
        cell: ({ row }) => {
          if (
            row.original?.Files?.find(
              (singleFile) =>
                singleFile.FileCategory?.CategoryName?.toLowerCase() ===
                'reference'
            )
          )
            return '✓';
          return null;
        },
      },
      {
        header: `${t('SearchCandidates.Table.Quality')}`,
        accessorKey: 'QualityStatus',
      },
      {
        header: `${t('SearchCandidates.Table.OpenToWork')}`,
        accessorKey: 'OpenToWork',
      },
      {
        accessorKey: 'OpenToWorkUpdatedOn',
        header: () => (
          <div>
            <div>{t('SearchCandidates.Table.OpenToWorkUpdatedOn')}</div>
            <div>({SEARCH_CANDIDATES_SHORTDATE})</div>
          </div>
        ),
        id: 'OpenToWorkUpdatedOn',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.OpenToWorkUpdatedOn &&
                `${dayjs(row.original.OpenToWorkUpdatedOn).format(
                  SEARCH_CANDIDATES_SHORTDATE
                )}`}
            </div>
          );
        },
      },
    ],
    [clickSortColumnHandler, columnSort, t]
  );

  const { getHeaderGroups, getRowModel } = useReactTable({
    data: SearchCandidateData?.data || [],
    columns,
    state: {
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    initialState: {
      pagination: {
        pageSize,
      },
    },
    getRowId: (row) => {
      return `${row.Id}`;
    },
  });

  const handleSelectRow = (candidateId: number) => {
    if (rowSelection[candidateId as keyof typeof rowSelection]) {
      setRowSelection((prev) => {
        delete prev[candidateId as keyof typeof rowSelection];
        return { ...prev };
      });
    } else {
      setRowSelection((prev) => ({ ...prev, [candidateId]: true }));
    }
  };

  const handleChangeAmmount = (ev: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(ev.target.value, 10));
    setPage(1);
  };

  const handleCellClick = (
    cell: Cell<SearchCandidateOutput['data'][number], unknown>,
    row: Row<SearchCandidateOutput['data'][number]>
  ) => {
    if (cell.column.id !== 'Email' && cell.column.id !== 'Id') {
      handleSelectRow(row.original.Id as number);
    }
  };

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    const searchedKeyword = newParams.get('Keyword') || '';
    // First time searching, save keywords
    if (!lastSearch.current) {
      lastSearch.current = searchedKeyword;
    }

    // If keywords has changed, save new keywords and reset to page 1
    if (lastSearch.current && lastSearch.current !== searchedKeyword) {
      lastSearch.current = searchedKeyword;
      setPage(1);
    }
    newParams.set('take', pageSize.toString());
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  }, [page, pageSize, searchParams, setSearchParams]);

  return (
    <div className="flex flex-row mt-2 w-full">
      <ModalInTailwind
        isOpen={isGeneratingCSV}
        closeModal={() => setIsGeneratingCSV(false)}
      >
        <div className="bg-white rounded p-8">
          <p className="text-xl mb-4">Generating CSV... Please wait</p>
          <Spinner height="100%" width="100%" />
        </div>
      </ModalInTailwind>
      <SearchCandidatesFilter />
      <div className="ml-4 pr-6 flex flex-col flex-1">
        <h1 className="text-4xl text-gray-500 mb-4">Search Candidates</h1>
        <div className="mb-4">
          <FilterInput title="Keyword" />
        </div>
        <div className="bg-white p-4 rounded">
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-100 z-50">
              {getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="text-left">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${header.id === 'select' && 'px-2'} py-2`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {!(isLoadingSearchCandidate || isFetchingSearchCandidate) && (
              <tbody className="divide-y-2">
                {getRowModel().rows.map((row) => {
                  return (
                    <tr
                      key={row.id}
                      className={`${
                        row.getIsSelected() && 'bg-internal-main/10'
                      } hover:bg-internal-main/30`}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td key={cell.id}>
                            <div
                              role="button"
                              tabIndex={0}
                              className={`${
                                (cell.column.id === 'select' ||
                                  cell.column.id === 'cv' ||
                                  cell.column.id === 'video') &&
                                'px-2'
                              } ${cell.column.id !== 'Id' && 'py-4'} ${
                                cell.column.id === 'Email' && 'cursor-auto'
                              }`}
                              onKeyDown={() => handleCellClick(cell, row)}
                              onClick={() => handleCellClick(cell, row)}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {(isLoadingSearchCandidate || isFetchingSearchCandidate) && (
          <div className="w-full overflow-hidden">
            <Spinner secondary width="100%" height="200px" />
          </div>
        )}
        <div className="ml-auto z-10 sticky bottom-0 items-center flex gap-8 bg-[#f1f3f6] px-4 rounded">
          <div className="flex items-center gap-4">
            <button
              type="button"
              disabled={page === 1}
              className="disabled:cursor-not-allowed disabled:opacity-25"
              onClick={() => setPage((prev) => prev - 1)}
            >
              <Arrow className="fill-zinc-600 w-8 h-8" />
            </button>
            {page} of {totalPages}
            <button
              type="button"
              disabled={page === totalPages || totalPages === 0}
              className="disabled:cursor-not-allowed disabled:opacity-25"
              onClick={() => setPage((prev) => prev + 1)}
            >
              <Arrow className="fill-zinc-600 w-8 h-8 rotate-180" />
            </button>
          </div>
          <label
            htmlFor="showCandidatesNumber"
            className="gap-2 flex items-center"
          >
            Show
            <select
              id="showCandidatesNumber"
              onChange={handleChangeAmmount}
              defaultValue={100}
              className="p-2"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
          <button
            type="button"
            className="bg-internal-main p-2"
            onClick={exportCandidates}
          >
            <Export className="w-6 h-6 fill-white rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchCandidates;
